import {
  ActionIcon,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconCashBanknote,
  IconChartArcs3,
  IconInfoCircle,
  IconReceipt2,
  IconFilter,
} from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import { MetricCardContainer } from 'components/common/MetricCardContainer';
import { InternalExportTableButton } from 'components/common/InternalExportTableButton';

import { PROVIDER_CLAIMS_SURFACE_COLOR } from './ProviderClaimsStyles';
import { BILLER_CLAIMS_CLASS_NAMES } from 'styles/BillerClaimsStyles';

type ClaimTotalTone = 'blue' | 'teal' | 'orange';

const providerTotalFallbackLabels: Record<string, string> = {
  'amount-submitted': 'Billed',
  'amount-paid': 'Paid',
  'amount-outstanding': 'Outstanding',
};

type ClaimTotalItem = {
  id: string;
  label: string;
  value: number;
  description: string;
  visibleClaimsLabel: string;
  detailsAriaLabel: string;
  icon: typeof IconReceipt2;
  tone: ClaimTotalTone;
};

interface ClaimTotalCardProps {
  item: ClaimTotalItem;
  formatCurrency: (value: number) => string;
}

interface ClaimTotalsPanelProps {
  submitted: number;
  paid: number;
  outstanding: number;
  formatCurrency: (value: number) => string;
  compact?: boolean;
  biller?: boolean;
  matchingClaims?: number;
}

const ClaimTotalCard = ({ item, formatCurrency }: ClaimTotalCardProps) => {
  const Icon = item.icon;

  return (
    <MetricCardContainer
      title={item.label}
      icon={<Icon size="1.3rem" stroke={2.2} />}
      accent={item.tone}
      testId={`claims-total-card-${item.id}`}
    >
      <Stack gap="sm" h="100%" justify="space-between">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text size="sm" c="dimmed" fw={500}>
            {item.visibleClaimsLabel}
          </Text>
          <Tooltip
            label={item.description}
            multiline
            w={220}
            withArrow
            position="top-end"
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={item.detailsAriaLabel}
            >
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Text fw={800} size="xl" data-testid={`claims-total-${item.id}`}>
          {formatCurrency(item.value)}
        </Text>
      </Stack>
    </MetricCardContainer>
  );
};

export const ClaimTotalsPanel = ({
  submitted,
  paid,
  outstanding,
  formatCurrency,
  compact = false,
  biller = false,
  matchingClaims = 0,
}: ClaimTotalsPanelProps) => {
  const { translate } = useTranslation();
  const visibleClaimsLabel = translate(
    'pages:claims.totals.visibleClaims',
    'Visible Claims'
  );
  const detailsAriaLabel = translate(
    'pages:claims.totals.detailsAriaLabel',
    'Claim total details'
  );
  const items: ClaimTotalItem[] = [
    {
      id: 'amount-submitted',
      label: translate(
        'pages:claims.totals.amountSubmitted.label',
        'Amount Submitted'
      ),
      value: submitted,
      description: translate(
        'pages:claims.totals.amountSubmitted.description',
        'The sum of tariff totals for the claims currently in the table.'
      ),
      visibleClaimsLabel,
      detailsAriaLabel,
      icon: IconReceipt2,
      tone: 'blue',
    },
    {
      id: 'amount-paid',
      label: translate('pages:claims.totals.amountPaid.label', 'Amount Paid'),
      value: paid,
      description: translate(
        'pages:claims.totals.amountPaid.description',
        'The sum of the paid amounts from debits for the claims currently in the table.'
      ),
      visibleClaimsLabel,
      detailsAriaLabel,
      icon: IconCashBanknote,
      tone: 'teal',
    },
    {
      id: 'amount-outstanding',
      label: translate(
        'pages:claims.totals.amountOutstanding.label',
        'Amount Outstanding'
      ),
      value: outstanding,
      description: translate(
        'pages:claims.totals.amountOutstanding.description',
        'The remaining balance for the claims currently in the table, calculated as submitted minus paid.'
      ),
      visibleClaimsLabel,
      detailsAriaLabel,
      icon: IconChartArcs3,
      tone: 'orange',
    },
  ];

  if (compact) {
    return (
      <Paper
        className={biller ? BILLER_CLAIMS_CLASS_NAMES.totals : undefined}
        withBorder
        radius="md"
        style={{
          overflow: 'hidden',
          background: PROVIDER_CLAIMS_SURFACE_COLOR,
        }}
        data-testid={biller ? 'biller-claim-totals' : 'provider-claim-totals'}
      >
        <Group
          justify="space-between"
          gap="sm"
          px="sm"
          py={6}
          wrap="nowrap"
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Group gap={6} wrap="nowrap">
            <IconFilter
              size={14}
              color="var(--mantine-primary-color-filled)"
              style={{ flexShrink: 0 }}
            />
            <Text size="xs" title={visibleClaimsLabel}>
              {translate(
                'pages:claims.providerTotals.filteredClaims',
                { total: matchingClaims },
                `Showing ${matchingClaims} of ${matchingClaims} claims based on your filters`
              )}
            </Text>
          </Group>
          <InternalExportTableButton
            label={translate('pages:claims.providerTotals.export', 'Export')}
          />
        </Group>
        <SimpleGrid cols={3} spacing={0}>
          {items.map((item, index) => (
            <Stack
              key={item.id}
              gap={2}
              px="sm"
              py="xs"
              miw={120}
              style={{
                borderLeft:
                  index > 0
                    ? '1px solid var(--mantine-color-default-border)'
                    : undefined,
              }}
            >
              <Text
                size="xs"
                c="dimmed"
                fw={700}
                tt="uppercase"
                style={{ letterSpacing: '0.1em' }}
              >
                {translate(
                  `pages:claims.providerTotals.${item.id}`,
                  providerTotalFallbackLabels[item.id]
                )}
              </Text>
              <Text fw={700} size="lg" data-testid={`claims-total-${item.id}`}>
                {formatCurrency(item.value)}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>
      </Paper>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
      {items.map((item) => (
        <ClaimTotalCard
          key={item.id}
          item={item}
          formatCurrency={formatCurrency}
        />
      ))}
    </SimpleGrid>
  );
};
